<template>
    <div class="banner-course-info">
        <div class="banner-course-info__container">
            <div class="banner-course-info__top">
                <div class="banner-course-info__header">
                    <span class="banner-course-info__microlearning-path" v-html="getMicroLearningSectionSubTitle"></span>
                    <h1 class="banner-course-info__title" v-html="getMicroLearningSectionTitle"></h1>
                </div>
                <ExportLSPContent v-if="course.canBeExportedToLSP" :course="course" />
            </div>
            <CourseDates :publishedDate="course.publishedDate" :expirationDate="course.expirationDate" :lastUpdatedDate="course.checkedAt" />
            <TotemAddToWatchlistButton
                v-if="showAddToFavourite"
                :elementId="course.id"
            />
            <span class="banner-course-info___breadcrumbs" v-if="course.breadcrumbs !== undefined">{{ course.breadcrumbs }}</span>

            <div class="banner-course-info__description -v-html" v-html="truncateDescription"></div>

            <div :class="['banner-course-info__actions']">
                <TotemBannerBack v-if="isDesktopView" />
                <span class="icon-arrow-left" @click="$router.go(-1)" v-if="isMobileView"></span>
                <div class="banner-course-info__read-more" @click="openDescriptionModal" v-if="showReadMore">
                    <span v-html="$t('General.read_more')"></span>
                    <span class="icon-plus"></span>
                </div>
                <CourseOverviewPackageInfo :hasCourseAccess="!course.demo" :isLoggedIn="isLoggedIn" v-if="isDesktopView"/>
            </div>
            <CourseOverviewPackageInfo :hasCourseAccess="!course.demo" :isLoggedIn="isLoggedIn" v-if="isMobileView"/>
        </div>

        <modal
            name="course-description"
            width="80%"
            :maxWidth="1200"
            height="auto"
            :adaptive="true"
            :scrollable="true"
        >
            <div class="banner-course-info__modal">
                <h1 class="banner-course-info__title">{{ course.title }}</h1>
                <span class="icon-clear banner-course-info__close-button" @click="closeModal"></span>
                <span class="banner-course-info__breadcrumbs" v-if="course.breadcrumbs !== undefined">{{ course.breadcrumbs }}</span>
                <div class="banner-course-info__description -modal -v-html" v-html="course.description"></div>
            </div>
        </modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import CourseDates from 'components/coursepage/courseaddons/CourseDates'
    import CourseOverviewPackageInfo from 'components/coursepage/courseoverview/CourseOverviewPackageInfo'
    import ExportLSPContent from 'components/coursepage/banner/ExportLSPContent'

    export default {
        components: {
            CourseDates,
            CourseOverviewPackageInfo,
            ExportLSPContent
        },
        props: {
            course: Object
        },
        data () {
            return {
                desktopDescLength: 650,
                mobileDescLength: 200
            }
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                currentMicroLearningSection: 'MicroLearning/getMicroLearningSection'
            }),
            showAddToFavourite () {
                return this.isLoggedIn
            },
            getTruncateLength () {
                return this.isDesktopView ? this.desktopDescLength : this.mobileDescLength
            },
            truncateDescription () {
                let length = this.isDesktopView ? this.desktopDescLength : this.mobileDescLength
                if (this.currentMicroLearningSection === null || this.currentMicroLearningSection.type === 'video') {
                    return this.truncate(this.course.description, length)
                } else {
                    return this.truncate(this.currentMicroLearningSection.description, length)
                }
            },
            showReadMore () {
                return this.isDesktopView ? this.course.description.length > this.desktopDescLength : this.course.description.length > this.mobileDescLength
            },
            getMicroLearningSectionSubTitle () {
                if (this.currentMicroLearningSection !== null) {
                    switch (this.currentMicroLearningSection.type) {
                    case 'video':
                        return this.course.title + ' / ' + this.getMicroLearningSectionTitle
                    case 'assignment':
                        return this.course.title + ' / ' + this.$t('Microlearning.assignments_title')
                    case 'file':
                        return this.course.title + ' / ' + this.$t('Microlearning.materials_title')
                    }
                }
                return this.course.title
            },
            getMicroLearningSectionTitle () {
                if (this.currentMicroLearningSection === null) {
                    return this.course.title
                } else {
                    return this.currentMicroLearningSection.title
                }
            }
        },
        methods: {
            openDescriptionModal () {
                this.$modal.show('course-description')
            },
            closeModal () {
                this.$modal.hide('course-description')
            },
            truncate (text, length) {
                if (text.length < length) {
                    return text
                } else {
                    return text.substring(0, length) + '...'
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .banner-course-info {
        position: relative;
        flex-grow: 1;

        &__container {
            padding: 30px 60px 30px 30px;

            [class*="desktop"] & {
                font-size: 2.8rem;
                padding: 30px 50px;
            }
        }

        &__top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        &__header {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
        }

        &__title {
            font-family: Quicksand-Bold, sans-serif;
            font-size: 2.4rem;
            padding: 0 0 30px;

            [class*="desktop"] & {
                padding: 0 0 15px;
            }
            [class*="mobile_sm"] & {
                font-size: 2.1rem;
                word-break: break-word;
            }
        }

        &__microlearning-path {
            font-size: 1.4rem;
            font-family: Nunito-Bold, sans-serif;
            color: $dusty_gray;
            margin: 0 0 5px;
        }

        &__breadcrumbs {
            font-size: 1.4rem;
            font-family: Nunito-Bold, sans-serif;
            color: $elm;
            margin: 0 0 20px;
            display: block;
        }

        &__description {
            font-size: 1.7rem;

            &.-modal {
                font-size: 1.7rem;
                line-height: 2.4rem;
            }
        }

        &__actions {
            display: flex;
            margin: 30px 0 0;
            align-items: center;
            justify-content: space-between;

            &.-without-cta-button {
                [class*="desktop"] & {
                    &:after {
                        content: '';
                        flex: 1;
                    }
                }
            }
        }

        .icon-arrow-left {
            font-size: 2.4rem;
            flex: 1;
            [class*="mobile"] & {
                flex: 0;
            }
        }

        &__read-more {
            font-size: 1.9rem;
            font-family: Nunito-Bold, sans-serif;
            flex: 1;
            text-align: center;
            white-space: nowrap;
            &:hover {
                cursor: pointer;
            }
        }

        .icon-plus {
            font-size: 1.8rem;
        }

        &__modal {
            padding: 40px 50px;
        }

        &__course-info {
            margin: 0 0 20px;
        }

        &__close-button {
            position: absolute;
            right: 15px;
            top: 15px;
            font-size: 4rem;
            color: $black;
            &:hover {
                opacity: 0.7;
            }
        }
    }
</style>
